import React from 'react';

// A clickable segment of an overview widget, e.g. VCM / Approved. `attribute`
// and `value` are the ransack attribute and value the Educators table is
// filtered by while the segment is active - see the status ransackers on User,
// which are the widget statuses the passport report can filter on.
export interface SegmentFilter {
  attribute: string;
  value: string;
  // Widget name, shown as the chip's prefix.
  group: string;
  // Segment name, shown under the stat and on the chip.
  label: string;
}

export const segmentFilterKey = (filter: SegmentFilter) => `${filter.attribute}:${filter.value}`;

export const segmentFilterLabel = (filter: SegmentFilter) => `${filter.group}: ${filter.label}`;

// Adds `filter` to `filters`, or removes it if it is already there.
export const toggleSegmentFilter = (
  filters: SegmentFilter[],
  filter: SegmentFilter
): SegmentFilter[] => {
  const key = segmentFilterKey(filter);

  return filters.some((active) => segmentFilterKey(active) === key)
    ? filters.filter((active) => segmentFilterKey(active) !== key)
    : [...filters, filter];
};

// Collapses the active segments into ransack params. Segments of the same
// widget are OR'd together with `_in`; a lone segment uses `_eq`, which is the
// predicate the passport report's status ransackers are already used with
// elsewhere (see PassportFilters).
//
// `has_registration` is the exception: it is not a ransacker but a filter of its
// own on the passport report, taking a comma separated list of statuses, since a
// registration's status spans several of its columns.
export const toSegmentRansackParams = (filters: SegmentFilter[]): Record<string, unknown> => {
  const valuesByAttribute = new Map<string, string[]>();

  filters.forEach((filter) => {
    const values = valuesByAttribute.get(filter.attribute) ?? [];
    values.push(filter.value);
    valuesByAttribute.set(filter.attribute, values);
  });

  const params: Record<string, unknown> = {};
  valuesByAttribute.forEach((values, attribute) => {
    if (attribute === 'has_registration') {
      params[attribute] = values.join(',');
    } else if (values.length === 1) {
      params[`${attribute}_eq`] = values[0];
    } else {
      params[`${attribute}_in`] = values;
    }
  });

  return params;
};

interface SegmentFilterContextValue {
  activeKeys: Set<string>;
  toggle: (filter: SegmentFilter) => void;
}

// Widgets are rendered generically from the dashboard payload, so the active
// filters reach SegmentItem through context rather than being threaded through
// every widget's props. Segments render as plain, unclickable stats when no
// provider is present.
export const SegmentFilterContext = React.createContext<SegmentFilterContextValue | null>(null);

export const useSegmentFilters = () => React.useContext(SegmentFilterContext);
